import { clientWindow } from "./dom";
import {
  THandlerParams,
  THandlerResults,
  HandlerName,
  TBaseResponse,
  AppRouterName,
  TAppRouterArguments,
  JoinAdTaskParams,
  AdTaskRequirementsCheckParams,
  PageControlActionName,
} from "@/shared";

export class BaseAppChannel {
  private static inAppCall<T extends HandlerName>(
    method: T,
    params?: THandlerParams[T]
  ): Promise<TBaseResponse<THandlerResults[T]>> | null {
    if (!clientWindow?.flutter_inappwebview) {
      return null;
    }
    return clientWindow.flutter_inappwebview.callHandler(method, params);
  }

  /**
   * 获取用户全部信息
   */
  static async getSessionInfoAsync() {
    const res = await this.inAppCall(HandlerName.GetSessionInfo);
    if (clientWindow && res) {
      clientWindow.yoofinds = res?.result;
    }
    return res;
  }

  /**
   * 客户端通道方法 - 获取客户端信息
   * @returns 客户端信息
   */
  static async getClientInfoAsync() {
    const res = await this.inAppCall(HandlerName.GetClientInfo);
    return res?.result;
  }

  /**
   * 客户端通道方法 - 获取token
   * @returns token
   */
  static async getTokenAsync() {
    const res = await this.inAppCall(HandlerName.GetToken);
    return res?.result;
  }

  /**
   * 客户端通道方法 - 获取用户信息
   * @returns 用户信息
   */
  static async getUserInfoAsync() {
    const res = await this.inAppCall(HandlerName.GetUserInfo);
    return res?.result;
  }

  /**
   * 客户端通道方法 - 获取用户uid
   * @returns 用户uid
   */
  static async getUidAsync() {
    const res = await this.inAppCall(HandlerName.GetUserInfo);
    return res?.result?.uid;
  }

  static async openSampleAuthorizeAsync(link: string) {
    const res = await this.inAppCall(HandlerName.OpenSampleAuthorize, link);
    return res;
  }

  static async goAppRouterNameAsync<T extends AppRouterName>(
    routerName: T,
    args?: TAppRouterArguments[T]
  ) {
    const res = await this.inAppCall(HandlerName.ToNamed, {
      routerName,
      arguments: args,
    });
    return res;
  }

  static async joinAdTaskAsync(taskInfo: JoinAdTaskParams) {
    const res = await this.inAppCall(HandlerName.JoinAdTask, taskInfo);
    return res;
  }

  static async adTaskRequirementsCheckAsync(
    taskInfo: AdTaskRequirementsCheckParams
  ) {
    const res = await this.inAppCall(
      HandlerName.AdTaskRequirementsCheck,
      taskInfo
    );
    return res;
  }

  static async loginAsync(middleContent?: string) {
    const res = await this.inAppCall(HandlerName.Login, {
      middle_content: middleContent,
    });
    return res;
  }

  static async setAppTitleAsync(title: string) {
    const res = await this.inAppCall(HandlerName.WebViewControl, {
      actionName: "setTitle",
      title,
    });
    return res;
  }

  static async setAppGoBackControlAsync() {
    const res = await this.inAppCall(HandlerName.WebViewControl, {
      actionName: "setBackModeToStack",
    });
    return res;
  }

  static async goBackAdTaskReloadAsync(url: string) {
    const res = await this.inAppCall(HandlerName.GoBackAdTaskReload, url);
    return res;
  }

  static async openWithNewWebViewAsync(url: string) {
    const res = await this.inAppCall(HandlerName.OpenWithNewWebView, {
      jump_url: url,
    });
    return res;
  }

  static async openWithExternalBrowserAsync(url: string) {
    const res = await this.inAppCall(HandlerName.OpenWithExternalBrowser, url);
    return res;
  }

  static async pageControlAsync(actionName: PageControlActionName) {
    const res = await this.inAppCall(HandlerName.PageControl, {
      actionName,
    });
    return res;
  }

  static async hideTitleBarAsync() {
    const res = await this.inAppCall(HandlerName.WebViewControl, {
      actionName: "hideTitleBar",
    });
    return res;
  }

  static async openMashupSelectScriptAsync(
    hash_id: string,
    product_id: string,
    page_source: string,
    ad_task_id?: string
  ) {
    const res = await this.inAppCall(HandlerName.ToNamed, {
      routerName: AppRouterName.MashupSelectScript,
      arguments: {
        hash_id,
        product_id,
        ad_task_id,
        page_source,
      },
    });
    return res;
  }
}
